import React from 'react';
import { useRecordContext } from 'react-admin';
import { Chip, Stack, Typography } from '@mui/material';
import { formatEur } from '../../Utils/money';
import { formatDateOnly, daysSince } from '../../Utils/date';
import {
	Badge,
	BillingAccountRow,
	PROCESS_STALE_DAYS,
	accountBadges,
	isEnterprisePlan,
	isSubscriptionActive,
} from './billingAlertsState';

/**
 * Props lues par le Datagrid sur ses enfants : `label` pour l'en-tête, `sortBy`
 * pour le tri serveur, `textAlign` pour l'alignement. Un champ sans `sortBy`
 * n'est pas triable, ce qui est voulu pour les colonnes dérivées de plusieurs
 * champs : le back ne sait pas les trier, autant ne pas rendre l'en-tête
 * cliquable pour rien.
 */
export interface CellProps {
	label?: string;
	sortBy?: string;
	textAlign?: 'left' | 'right';
}

const SUBLINE_TONES = { error: 'error.main', warn: 'warning.main' } as const;

/** Une valeur qui n'existe pas, ou qu'on ne sait pas. Jamais un zéro déguisé. */
export const Dash: React.FC = () => (
	<Typography component="span" color="text.disabled" fontSize="inherit">
		—
	</Typography>
);

/**
 * ⚠️ `measuredDays === 0` ne s'affiche JAMAIS « 0,00 € ». Zéro euro se lit
 * « ce projet ne nous coûte rien », alors que la vérité est « rien n'a été
 * mesuré ». Les deux se décident à l'opposé.
 *
 * Même libellé pour un projet non managé sans conso : distinguer les deux
 * obligerait à lire une autre colonne pour savoir de quoi la cellule parle.
 */
export const MeasuredAmount: React.FC<{
	value: number;
	measuredDays: number;
	alarming?: boolean;
}> = ({ value, measuredDays, alarming }) => {
	if (!measuredDays) {
		return (
			<Typography component="span" color="text.disabled" fontSize="inherit">
				non mesuré
			</Typography>
		);
	}
	return (
		// `fontSize: inherit` partout dans les cellules : le variant par défaut de
		// Typography vaut 1rem et écrase la densité posée sur le `<td>`, ce qui
		// donnait des montants deux crans plus gros que le reste de la ligne.
		<Typography
			component="span"
			color={alarming && value ? 'error.main' : 'text.primary'}
			fontWeight={alarming && value ? 600 : 400}
			fontSize="inherit"
		>
			{formatEur(value)}
		</Typography>
	);
};

export const StateChips: React.FC<{ badges: Badge[] }> = ({ badges }) => (
	<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
		{badges.map((badge) => (
			<Chip
				key={badge.key}
				label={badge.label}
				size="small"
				color={badge.color}
				variant="outlined"
			/>
		))}
	</Stack>
);

export const SubLine: React.FC<{
	children: React.ReactNode;
	tone?: 'error' | 'warn';
}> = ({ children, tone }) => (
	<Typography
		variant="caption"
		display="block"
		color={tone ? SUBLINE_TONES[tone] : 'text.secondary'}
		fontWeight={tone ? 600 : 400}
		sx={{ fontSize: '0.75rem' }}
	>
		{children}
	</Typography>
);

export const AccountNameField: React.FC<CellProps> = () => {
	const record = useRecordContext<BillingAccountRow>();
	if (!record) return null;
	return (
		<Typography variant="body2" fontWeight={600} fontSize="inherit">
			{record.name}
		</Typography>
	);
};

/**
 * ⚠️ C'est le statut Stripe qui porte l'alerte, en rouge et en gras, et plus
 * aucun badge ne le redit. Le mettre dans la colonne où on va déjà chercher la
 * formule le fait lire sans le chercher.
 */
export const SubscriptionField: React.FC<CellProps> = () => {
	const record = useRecordContext<BillingAccountRow>();
	if (!record) return null;
	const dead = !isSubscriptionActive(record.subscriptionStatus);
	return (
		<>
			<Typography variant="body2" fontSize="inherit">{record.billingPlanName || '—'}</Typography>
			{/* Un compte n'a pas toujours d'abonnement du tout : le dire, plutôt qu'un
			    tiret rouge qu'on lirait comme une donnée manquante. */}
			<SubLine tone={dead ? 'error' : undefined}>
				{record.subscriptionStatus || 'aucun abonnement'}
			</SubLine>
		</>
	);
};

export const ProjectsField: React.FC<CellProps> = () => {
	const record = useRecordContext<BillingAccountRow>();
	if (!record) return null;
	return (
		<>
			<Typography variant="body2" fontSize="inherit">{record.projectCount}</Typography>
			{record.managedProjectCount > 0 && (
				<SubLine>
					dont {record.managedProjectCount} managé
					{record.managedProjectCount > 1 ? 's' : ''}
				</SubLine>
			)}
		</>
	);
};

/**
 * Le dernier process : le seul signe que le datawarehouse qu'on paie sert encore.
 * Les runs `test_mode` sont exclus côté back — déclenchés par l'équipe, ils
 * feraient passer un projet mort pour vivant.
 *
 * L'ancienneté est teintée au-delà du seuil plutôt que badgée : même
 * information, sans pastille qui répète la colonne.
 */
export const LastProcess: React.FC<{ at: string | null }> = ({ at }) => {
	if (!at) {
		return (
			<Typography component="span" color="text.disabled" fontSize="inherit">
				jamais
			</Typography>
		);
	}
	const days = daysSince(at);
	const stale = days !== null && days > PROCESS_STALE_DAYS;
	return (
		<>
			<Typography variant="body2" fontSize="inherit">
				{formatDateOnly(at)}
			</Typography>
			{days !== null && <SubLine tone={stale ? 'warn' : undefined}>il y a {days} j</SubLine>}
		</>
	);
};

export const LastProcessField: React.FC<CellProps> = () => {
	const record = useRecordContext<BillingAccountRow>();
	if (!record) return null;
	return <LastProcess at={record.lastProcessAt} />;
};

export const StorageField: React.FC<CellProps> = () => {
	const record = useRecordContext<BillingAccountRow>();
	if (!record) return null;
	return <MeasuredAmount value={record.storageEur} measuredDays={record.measuredDays} />;
};

export const ManagedCostField: React.FC<CellProps> = () => {
	const record = useRecordContext<BillingAccountRow>();
	if (!record) return null;
	return (
		<MeasuredAmount
			value={record.totalEur}
			measuredDays={record.measuredDays}
			alarming={record.alert}
		/>
	);
};

export const AccountStateField: React.FC<CellProps> = () => {
	const record = useRecordContext<BillingAccountRow>();
	if (!record) return null;
	return <StateChips badges={accountBadges(record)} />;
};
